import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable, Subject, take } from 'rxjs';
import { CommunicationService } from '../../../communication/communication.service';
import { UserPreferences } from '../../../models/data-structures/user-preferences.model';
import { ConfiguredToastrService } from '../../../helper/toastr/configured-toastr.service';
import { AnessaTranslationService } from '../../../helper/translation/anessa-translation.service';
import Utils from '../../../util';
import { takeUntil } from 'rxjs/operators';


@Injectable({
  providedIn: 'root'
})
export class PreferencesService implements OnDestroy {
  private onDestroyNotifier = new Subject<void>();

  // notifier for user preferences updating after saving and fetching
  preferencesUpdatedNotifier = new BehaviorSubject<boolean>(false);

  // current user preferences object
  private userPreferences: UserPreferences | null = null;

  constructor(private commService: CommunicationService,
              private toastrService: ConfiguredToastrService,
              private translationService: AnessaTranslationService,
  ) {
    // if (we have the user ID in localstorage we can fetch preferences
    // right away
    if (Utils.getUserID()) {
      this.fetchPreferences();
    }
  }

  /**
   * Performs subscription cleanup.
   */
  ngOnDestroy() {
    this.onDestroyNotifier.next();
    this.onDestroyNotifier.complete();
  }

  /**
   * Starts user preference fetch unless user preferences are already loaded
   * and ready
   */
  initializeUserPreferences() {
    // if not already loaded
    if (this.preferencesUpdatedNotifier.value !== true) {
      this.fetchPreferences();
    }
  }

  /**
   * Unloads user preferences from this service.
   */
  unloadUserPreferences() {
    this.userPreferences = null;
    this.preferencesUpdatedNotifier.next(false);
  }

  /**
   * Save the provided user preferences to the DB, returns a boolean promise (async)
   * @param userPreferences
   */
  async saveUserPreferences(userPreferences: UserPreferences): Promise<boolean> {
    const res = await firstValueFrom((this.commService.postRequest(
      {'userPreferences': userPreferences},
      'dbapi/user/saveADAUserPreferences') as Observable<{ success: boolean }>)
      .pipe(takeUntil(this.onDestroyNotifier)));

    if (res['success']) {
      this.userPreferences = new UserPreferences(userPreferences);
      const translation = this.translationService.get('DROPDOWN.USER_PREFERENCES.SUCCESS.SAVED');
      this.toastrService.success(translation);
      this.preferencesUpdatedNotifier.next(true);
      return true;
    }

    Error(this.translationService.get('DROPDOWN.USER_PREFERENCES.ERROR.FAILED_TO_SAVE'));
    return false;
  }

  /**
   * Get the currently loadd user preference object, creates a deep copy
   */
  getUserPreferences() {
    if (this.userPreferences && this.preferencesUpdatedNotifier.value) {
      return UserPreferences.deepCopy(this.userPreferences);
    } else {
      throw Error('Unable to get user preferences before they have been loaded');
    }
  }

  /**
   * Fetch and initilize user preferences
   */
  private fetchPreferences() {
    // get all, the controller will have the user id from the request already
    this.commService.getAllRequest('dbapi/user/fetchADAUserPreferences')
      .pipe(take(1))
      .subscribe((res: any) => {
        // if null then create a new object for user
        this.userPreferences = new UserPreferences(res['obj']);
        console.log('user preferences loaded');
        this.preferencesUpdatedNotifier.next(true);
      });
  }
}
